import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import logo from "@/assets/logo.webp";
import hero800 from "@/assets/hero-800.webp";
import hero1400 from "@/assets/hero-1400.webp";

const optImages = import.meta.glob("../assets/opt/*.webp", {
  eager: true,
  query: "?url",
  import: "default",
}) as Record<string, string>;

function optUrl(filename: string) {
  const entry = Object.entries(optImages).find(([key]) => key.endsWith(`/${filename}`));
  if (!entry) throw new Error(`Missing optimized image ${filename}`);
  return entry[1];
}

/** Smallest width is the fallback `src`. Wider files are only fetched when the slot needs them. */
function picture(stem: string, widths: number[], fullW: number, fullH: number) {
  const files = widths.map((w) => ({ w, url: optUrl(`${stem}-w${w}.webp`) }));
  const smallest = files[0];
  return {
    src: smallest.url,
    srcSet: files.length > 1 ? files.map((f) => `${f.url} ${f.w}w`).join(", ") : undefined,
    w: smallest.w,
    h: Math.round((fullH * smallest.w) / fullW),
  };
}

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Scott Ranch Sand & Gravel | Excavation, Driveways & Ponds — Ellis County, TX" },
      { name: "description", content: "Family-owned Ellis County excavation contractor. Driveway & culvert install, pool demo, pond builds, tree removal, aggregate delivery. Owner on every job. Call (214) 616-4452." },
      { property: "og:title", content: "Scott Ranch Sand & Gravel — Ellis County Excavation" },
      { property: "og:description", content: "Voted #1 Excavating Contractor in Ellis County. Honest pricing, owner on every job." },
      { property: "og:url", content: "https://scottranchsandgravel.com/" },
      { property: "og:locale", content: "en_US" },
      { name: "twitter:title", content: "Scott Ranch Sand & Gravel | Excavation, Driveways & Ponds — Ellis County, TX" },
      { name: "twitter:description", content: "Family-owned Ellis County excavation contractor. Driveway & culvert install, pool demo, pond builds, tree removal, aggregate delivery. Owner on every job. Call (214) 616-4452." },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
    ],
    links: [
      { rel: "canonical", href: "https://scottranchsandgravel.com/" },
      {
        rel: "preload",
        as: "image",
        href: hero1400,
        imageSrcSet: `${hero800} 800w, ${hero1400} 1400w`,
        imageSizes: "100vw",
        fetchPriority: "high",
      },
    ],
  }),
  component: Home,
});

const PHONE = "(214) 616-4452";
const TEL = "+12146164452";
const SMS = "12146164452";
const FB = "https://www.facebook.com/p/Scott-Ranch-Sand-and-Gravel-100064129207992/";
const GBP = "https://share.google/y4z0feBHirVJVIw81";

const services = [
  { t: "Driveway Install", d: "Crushed concrete, gravel, base — packed firm, finished clean.", n: "01" },
  { t: "Culvert Install", d: "Proper drainage and approach work that holds up to Texas weather.", n: "02" },
  { t: "Pool Demolition", d: "Complete pool removal and site restoration done right.", n: "03" },
  { t: "Pond Builds & Repairs", d: "New ponds, dam fixes, leveling and shoreline cleanup.", n: "04" },
  { t: "Excavation", d: "Site prep, grading, pads, septic restoration and dirt work.", n: "05" },
  { t: "Tree Removal", d: "Clearing for lots, lanes, fence lines and pond access.", n: "06" },
  { t: "Creek Crossings", d: "Low-water crossings engineered for ranch and rural roads.", n: "07" },
  { t: "Aggregate Delivery", d: "Sand, gravel, base, crushed concrete — delivered to your project.", n: "08" },
];

const reviews = [
  {
    body: "The owner came out & gave me a quote for a project I was needing done. After we agreed on the scope of work & a start date he showed up on time & did a awesome job! He was very professional & showed up when he said he would. I would highly recommend Randy & his services!",
  },
  {
    body: "We hired Randy to redo our driveway with crushed concrete and fix our yard after a septic install. He did a superior job! He is prompt, efficient and reasonably priced. Highly recommend.",
  },
  {
    body: "We couldn't be happier with our new driveway and pad to park on, it turned out great! He gave us a firm date, showed up right on time both days, and was professional and friendly. The work he does is top-notch. He packed it down firmly and even finished with a push-broom so the surface looks clean and uniform. He did exactly what we wanted and exceeded our expectations. We will absolutely call him back out to complete other work for us in the future.",
  },
];

const PAIR_SIZES = "(min-width: 768px) 640px, 100vw";

const yardArt = picture("photo-920f6649", [800, 1280], 1600, 1200);
const awardImg = picture("award", [640], 1014, 1920);

const galleryPairs = [
  {
    label: "Pool Demolition",
    a: { ...picture("pool-demo", [800, 1280], 1280, 960), alt: "Backyard pool demolition in progress with excavator on site", caption: "Before" },
    b: { ...picture("pad-prep", [800, 1280], 1600, 1200), alt: "Pool site fully removed, backfilled and graded to a clean sand pad", caption: "After" },
  },
  {
    label: "Yard Art Display",
    a: { ...picture("photo-889c9e76", [800, 1280], 1600, 1200), alt: "SANY excavator and dump truck prepping the yard art display site", caption: "Before" },
    b: { ...yardArt, alt: "Finished yard art display with vintage tractors and antique plows on a fresh pad", caption: "After" },
  },
  {
    label: "Culvert Install / Replacement",
    a: { ...picture("culvert", [600], 600, 800), alt: "Mini excavator setting twin metal culverts at a driveway entrance", caption: "Before" },
    b: { ...picture("driveway-approach", [800], 800, 600), alt: "Finished gravel driveway approach over the new culvert install", caption: "After" },
  },
  {
    label: "Pond / Dam Repair",
    a: { ...picture("photo-aad138f0", [800, 1280], 1600, 1200), alt: "SANY excavator digging fresh dirt along the pond bank for a dam repair", caption: "Before" },
    b: { ...picture("photo-498447e8", [800, 1280], 1600, 1200), alt: "Repaired pond dam with SANY excavator and dump truck on a packed access lane", caption: "After" },
  },
  {
    label: "Koi Pond Demo",
    a: { ...picture("pond-build", [800, 1280], 1280, 960), alt: "Backyard koi pond dig in progress with skid steer and stacked stone surround", caption: "Before" },
    b: { ...picture("sod-install", [800, 1280], 1280, 960), alt: "Finished koi pond area with fresh sod laid next to the flagstone patio", caption: "After" },
  },
  {
    label: "Water Drain",
    a: { ...picture("dumptruck-excavator", [800, 1024], 1024, 768), alt: "Dump truck and SANY excavator digging a drainage trench", caption: "Excavation" },
    b: { ...picture("trucks-yard", [800], 800, 600), alt: "Finished commercial truck yard with new drainage grate and crushed base", caption: "After" },
  },
];

const gallerySingles = [
  { ...picture("photo-4fa35440", [800, 1280], 1600, 1200), label: "Culvert Install", alt: "Rock-lined culvert install under the tree line with mini excavator staged behind" },
  {
    src: hero800,
    srcSet: `${hero800} 800w, ${hero1400} 1400w`,
    w: 800,
    h: 600,
    label: "Commercial Parking Lot",
    alt: "Roller compactor and dump truck finishing a commercial parking lot",
  },
  { ...picture("land-clearing", [800, 1280], 1600, 1200), label: "New Pond Construction", alt: "Dump truck, skid steer and dozer shaping a new pond next to a covered arena" },
  { ...picture("trench", [800, 1280], 1600, 1200), label: "Water Line Trench", alt: "Long water line trench cut across a North Texas pasture" },
  { ...picture("dump-sunset", [800, 1024], 1024, 768), label: "Select Fill Dirt Delivery", alt: "Dump truck delivering select fill dirt at golden hour" },
  { ...picture("dump-dusk", [800, 1024], 1024, 768), label: "House Pad Construction", alt: "Dump truck spreading fill material for a new house pad" },
];



function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll<HTMLElement>(".reveal");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -60px 0px" }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
}

function Home() {
  useReveal();
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Nav />
      <Hero />
      <Marquee />
      <Services />
      <About />
      <Award />
      <Gallery />
      <Reviews />
      <Contact />
      <Footer />
    </div>
  );
}

function Nav() {
  return (
    <header className="sticky top-0 z-50 border-b-2 border-ink/90 bg-background/85 backdrop-blur">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3">
        <a href="#top" className="flex min-w-0 items-center gap-3">
          <img src={logo} alt="Scott Ranch Sand & Gravel horseshoe logo" className="h-11 w-11 shrink-0 object-contain" width={160} height={160} />
          <div className="min-w-0 leading-tight">
            <div className="font-display text-xl tracking-wide">Scott Ranch</div>
            <div className="hidden text-[10px] uppercase tracking-[0.22em] text-muted-foreground sm:block">Sand &amp; Gravel · Est. Ellis County</div>
          </div>
        </a>
        <nav className="hidden items-center gap-7 text-sm font-medium md:flex">
          <a href="#services" className="hover:text-accent">Services</a>
          <a href="#about" className="hover:text-accent">About</a>
          <a href="#gallery" className="hover:text-accent">Gallery</a>
          <a href="#reviews" className="hover:text-accent">Reviews</a>
          <a href="#contact" className="hover:text-accent">Contact</a>
        </nav>
        <a href={`tel:${TEL}`} className="btn-rust shrink-0 rounded-md px-4 py-2 text-sm font-bold uppercase tracking-wider">
          <span className="sm:hidden">Call</span>
          <span className="hidden sm:inline">Call {PHONE}</span>
        </a>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section id="top" className="relative overflow-hidden border-b-2 border-ink/90">
      <div className="absolute inset-0">
        <img
          src={hero1400}
          srcSet={`${hero800} 800w, ${hero1400} 1400w`}
          sizes="100vw"
          alt=""
          aria-hidden
          width={1400}
          height={1050}
          fetchPriority="high"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/55 via-ink/35 to-ink/85" />
      </div>
      <div className="relative mx-auto grid max-w-7xl gap-10 px-5 py-24 md:grid-cols-12 md:py-36">
        <div className="md:col-span-8">
          <div className="inline-flex items-center gap-2 rounded-full border border-bone/40 bg-ink/30 px-3 py-1 text-[11px] uppercase tracking-[0.25em] text-bone backdrop-blur">
            <span className="h-1.5 w-1.5 rounded-full bg-accent" />
            Family Owned · Ellis County, TX
          </div>
          <h1 className="mt-6 font-display text-[14vw] leading-[0.88] text-bone md:text-[110px]">
            Dirt work,<br />done<span className="text-accent"> right.</span>
          </h1>
          <p className="mt-6 max-w-xl text-lg text-bone/90">
            Driveways, culverts, pool demo, ponds, and aggregate delivery — the owner is on every job.
            Honest quotes, on-time crews, no money up front.
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-widest text-bone/70">
            <span className="rounded-full border border-bone/30 px-2.5 py-1">Residential & Commercial</span>
            <span className="rounded-full border border-bone/30 px-2.5 py-1">Fully Insured</span>
            <span className="rounded-full border border-bone/30 px-2.5 py-1">Bonded</span>
          </div>
          <div className="mt-8 flex flex-wrap gap-4">
            <a href={`tel:${TEL}`} className="btn-rust rounded-md px-6 py-3 text-sm font-bold uppercase tracking-wider">
              Call {PHONE}
            </a>
            <a href={`sms:${SMS}`} className="btn-ink rounded-md px-6 py-3 text-sm font-bold uppercase tracking-wider">
              Text for a Free Quote
            </a>
          </div>
          <dl className="mt-12 grid max-w-lg grid-cols-3 gap-6 border-t border-bone/25 pt-6 text-bone">
            <div><dt className="text-[11px] uppercase tracking-widest text-bone/60">Rated</dt><dd className="font-display text-3xl">#1</dd><dd className="text-xs text-bone/70">Ellis County</dd></div>
            <div><dt className="text-[11px] uppercase tracking-widest text-bone/60">Crews</dt><dd className="font-display text-3xl">Owner-run</dd><dd className="text-xs text-bone/70">No sub-contractors</dd></div>
            <div><dt className="text-[11px] uppercase tracking-widest text-bone/60">Upfront</dt><dd className="font-display text-3xl">$0</dd><dd className="text-xs text-bone/70">Pay on completion</dd></div>
          </dl>
        </div>
      </div>
    </section>
  );
}

function Marquee() {
  const items = ["Driveways", "Culverts", "Pool Demo", "Ponds", "Excavation", "Aggregate", "Tree Removal", "Creek Crossings"];
  const row = [...items, ...items];
  return (
    <div className="overflow-hidden border-b-2 border-ink/90 bg-ink py-5 text-bone">
      <div className="marquee-track flex w-max gap-12 whitespace-nowrap">
        {row.map((s, i) => (
          <span key={i} className="flex items-center gap-12 font-display text-3xl tracking-wide">
            {s}
            <span className="text-accent">✦</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function Services() {
  return (
    <section id="services" className="relative mx-auto max-w-7xl px-5 py-24">
      <div className="reveal mb-14 flex flex-wrap items-end justify-between gap-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-accent">What We Do</div>
          <h2 className="mt-2 font-display text-5xl md:text-6xl">Built for the work.</h2>
        </div>
        <p className="max-w-md text-muted-foreground">
          One stop shop for everything from a fresh driveway to a brand-new pond. Hourly rates,
          day rates, or a free quote — your call.
        </p>
      </div>
      <ul className="grid gap-px overflow-hidden rounded-xl border-2 border-ink bg-ink md:grid-cols-2 lg:grid-cols-4">
        {services.map((s, i) => (
          <li
            key={s.t}
            className={`reveal reveal-delay-${(i % 4) + 1} group relative bg-background p-7 transition-colors hover:bg-secondary`}
          >
            <div className="font-display text-sm tracking-widest text-accent">{s.n}</div>
            <h3 className="mt-3 font-display text-2xl">{s.t}</h3>
            <p className="mt-2 text-sm text-muted-foreground">{s.d}</p>
            <div className="mt-6 h-[2px] w-8 bg-ink transition-all duration-300 group-hover:w-20 group-hover:bg-accent" />
          </li>
        ))}
      </ul>
    </section>
  );
}

function About() {
  return (
    <section id="about" className="relative border-y-2 border-ink/90 bg-secondary">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 py-24 md:grid-cols-2 md:items-center">
        <div className="reveal relative">
          <img
            src={yardArt.src}
            srcSet={yardArt.srcSet}
            sizes="(min-width: 768px) 560px, 100vw"
            alt="The Scott Ranch yard with tractors, dump trucks, excavator and skid steer"
            className="hard-shadow w-full rounded-md border-2 border-ink object-cover"
            loading="lazy"
            decoding="async"
            width={yardArt.w}
            height={yardArt.h}
          />
          <div className="absolute -bottom-5 -right-5 hidden rotate-3 rounded-md border-2 border-ink bg-accent px-4 py-3 text-bone shadow-[5px_5px_0_0_var(--ink)] md:block">
            <div className="font-display text-xl leading-none">Owner on site.</div>
            <div className="text-[11px] uppercase tracking-widest">Every job. No exceptions.</div>
          </div>
        </div>
        <div className="reveal reveal-delay-1">
          <div className="text-[11px] uppercase tracking-[0.3em] text-accent">About</div>
          <h2 className="mt-2 font-display text-5xl md:text-6xl">Honest hands. Heavy iron.</h2>
          <p className="mt-5 text-lg text-foreground/90">
            Scott Ranch Sand &amp; Gravel is a family-owned excavation outfit serving Ellis County and
            the surrounding North Texas communities. Randy Scott runs the equipment himself — so the
            person quoting your project is the same one finishing it.
          </p>
          <p className="mt-4 text-foreground/80">
            We keep it simple: show up when we say we will, charge what we quote, and don't ask for a
            dime until the work is done. No sub-contractors, no surprises.
          </p>
          <div className="mt-4 inline-flex flex-wrap gap-2 text-[11px] uppercase tracking-widest text-muted-foreground">
            <span className="rounded-full border border-ink px-2.5 py-1">Residential & Commercial</span>
            <span className="rounded-full border border-ink px-2.5 py-1">Fully Insured</span>
            <span className="rounded-full border border-ink px-2.5 py-1">Bonded</span>
          </div>
          <ul className="mt-8 grid gap-3 text-sm">
            {["No money up front", "Owner-operated, never sub'd out", "Free on-site quotes", "Hourly, day rate, or flat bid"].map((p) => (
              <li key={p} className="flex items-center gap-3">
                <span className="font-display text-accent">▶</span> {p}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function Award() {
  return (
    <section className="relative overflow-hidden bg-ink text-bone">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 py-20 md:grid-cols-12">
        <div className="reveal md:col-span-7">
          <div className="text-[11px] uppercase tracking-[0.3em] text-accent">Recognized</div>
          <h2 className="mt-2 font-display text-5xl text-bone md:text-6xl">
            Voted #1 Excavating Contractor in Ellis County.
          </h2>
          <p className="mt-5 max-w-xl text-bone/80">
            BusinessRate's Best of 2026 — powered by Google Reviews. Earned the old-fashioned way:
            one job, one happy customer at a time.
          </p>
          <div className="mt-6 flex flex-wrap gap-3 text-xs uppercase tracking-widest">
            <span className="rounded-full border border-bone/30 px-3 py-1">Earned</span>
            <span className="rounded-full border border-bone/30 px-3 py-1">Respected</span>
            <span className="rounded-full border border-accent bg-accent/15 px-3 py-1 text-accent">Recognized</span>
          </div>
        </div>
        <div className="reveal reveal-delay-1 md:col-span-5">
          <img
            src={awardImg.src}
            alt="Scott Ranch Sand & Gravel ranked #1 in Ellis County — BusinessRate Best of 2026"
            className="mx-auto max-h-[480px] w-auto rounded-md border-2 border-bone/20 bg-bone p-4"
            loading="lazy"
            decoding="async"
            width={awardImg.w}
            height={awardImg.h}
          />
        </div>
      </div>
    </section>
  );
}

function Gallery() {
  return (
    <section id="gallery" className="mx-auto max-w-7xl px-5 py-24">
      <div className="reveal mb-12 flex flex-wrap items-end justify-between gap-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-accent">Recent Work</div>
          <h2 className="mt-2 font-display text-5xl md:text-6xl">From the yard.</h2>
        </div>
        <p className="max-w-md text-muted-foreground">Driveways, ponds, culverts and dirt work — straight from the job site.</p>
      </div>

      {/* Before / After pairs */}
      <div className="space-y-12">
        {galleryPairs.map((pair, idx) => (
          <div key={idx} className="reveal">
            <div className="mb-3 text-[11px] uppercase tracking-[0.25em] text-accent">{pair.label}</div>
            <div className="grid gap-4 md:grid-cols-2">
              {[pair.a, pair.b].map((p, i) => (
                <figure key={i} className="group relative overflow-hidden rounded-md border-2 border-ink">
                  <img
                    src={p.src}
                    srcSet={p.srcSet}
                    sizes={PAIR_SIZES}
                    alt={p.alt}
                    width={p.w}
                    height={p.h}
                    loading="lazy"
                    decoding="async"
                    className="h-[260px] w-full object-cover transition-transform duration-700 group-hover:scale-105 md:h-[320px]"
                  />
                  <div className="absolute bottom-3 left-3 rounded-md border border-ink bg-ink/80 px-3 py-1 text-[11px] uppercase tracking-widest text-bone backdrop-blur">
                    {p.caption}
                  </div>
                </figure>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Single photos */}
      <div className="mt-12 grid auto-rows-[220px] grid-cols-2 gap-4 md:grid-cols-4">
        {gallerySingles.map((g, i) => {
          const span = i === 0 ? "col-span-2 row-span-2" : "col-span-2 md:col-span-1";
          return (
            <figure
              key={i}
              className={`group relative overflow-hidden rounded-md border-2 border-ink ${span}`}
            >
              <img
                src={g.src}
                srcSet={g.srcSet}
                sizes={i === 0 ? "(min-width: 768px) 640px, 100vw" : "(min-width: 768px) 320px, 50vw"}
                alt={g.alt}
                width={g.w}
                height={g.h}
                loading="lazy"
                decoding="async"
                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute bottom-3 left-3 rounded-md border border-ink bg-ink/80 px-3 py-1 text-[11px] uppercase tracking-widest text-bone backdrop-blur">
                {g.label}
              </div>
            </figure>
          );
        })}
      </div>

    </section>
  );
}

function Reviews() {
  return (
    <section id="reviews" className="border-y-2 border-ink/90 bg-secondary">
      <div className="mx-auto max-w-7xl px-5 py-24">
        <div className="reveal mb-12 flex flex-wrap items-end justify-between gap-6">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-accent">Word of Mouth</div>
            <h2 className="mt-2 font-display text-5xl md:text-6xl">Straight from the neighbors.</h2>
          </div>
          <div className="flex items-center gap-2 text-accent">
            {"★★★★★".split("").map((s, i) => <span key={i} className="text-2xl">{s}</span>)}
            <span className="ml-2 text-sm font-medium text-muted-foreground">5.0 on Google</span>
          </div>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {reviews.map((r, i) => (
            <blockquote
              key={i}
              className={`reveal reveal-delay-${i + 1} hard-shadow flex flex-col rounded-md border-2 border-ink bg-card p-7`}
            >
              <div className="font-display text-4xl leading-none text-accent">"</div>
              <p className="mt-2 flex-1 text-foreground/90">{r.body}</p>
              <footer className="mt-6 flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
                <span className="text-accent">★★★★★</span> Verified Google Review
              </footer>
            </blockquote>
          ))}
        </div>
        <div className="reveal mt-12 text-center">
          <a href={GBP} target="_blank" rel="noopener noreferrer" className="btn-ink inline-block rounded-md px-7 py-3 text-sm font-bold uppercase tracking-wider">
            Read more reviews on Google →
          </a>
        </div>
      </div>
    </section>
  );
}

function Contact() {
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const payload = {
      name: String(fd.get('name') || '').trim(),
      email: String(fd.get('email') || '').trim(),
      phone: String(fd.get('phone') || '').trim(),
      message: String(fd.get('message') || '').trim(),
      website: String(fd.get('website') || ''),
    };
    if (!payload.name || !payload.email || !payload.message) {
      setStatus('error');
      setErrorMsg('Please fill in your name, email, and a message.');
      return;
    }
    setStatus('sending');
    setErrorMsg('');
    try {
      const res = await fetch('/api/public/hooks/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        setStatus('error');
        setErrorMsg(data?.error || 'Something went wrong. Please call or text instead.');
        return;
      }
      setStatus('success');
      form.reset();
    } catch {
      setStatus('error');
      setErrorMsg('Network error. Please call or text instead.');
    }
  }

  return (
    <section id="contact" className="relative overflow-hidden bg-background">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 py-24 md:grid-cols-2">
        <div className="reveal">
          <div className="text-[11px] uppercase tracking-[0.3em] text-accent">Let's Talk</div>
          <h2 className="mt-2 font-display text-5xl md:text-6xl">Call, text, or send a message.</h2>
          <p className="mt-5 max-w-md text-muted-foreground">
            Reach Randy directly — he'll come measure your project and write up an honest quote on the spot.
          </p>
          <div className="mt-10 space-y-4">
            <a href={`tel:${TEL}`} className="flex items-center justify-between rounded-md border-2 border-ink bg-card p-5 transition hover:bg-accent hover:text-accent-foreground">
              <div>
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Phone</div>
                <div className="font-display text-3xl">{PHONE}</div>
              </div>
              <span className="font-display text-2xl">→</span>
            </a>
            <a href={`sms:${SMS}`} className="flex items-center justify-between rounded-md border-2 border-ink bg-card p-5 transition hover:bg-accent hover:text-accent-foreground">
              <div>
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Text</div>
                <div className="font-display text-3xl">{PHONE}</div>
              </div>
              <span className="font-display text-2xl">→</span>
            </a>
            <a href="mailto:scottranchautosales@gmail.com" className="flex items-center justify-between rounded-md border-2 border-ink bg-card p-5 transition hover:bg-accent hover:text-accent-foreground">
              <div>
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Email</div>
                <div className="font-display text-2xl break-all">scottranchautosales@gmail.com</div>
              </div>
              <span className="font-display text-2xl">→</span>
            </a>
          </div>
        </div>
        <div className="reveal reveal-delay-1">
          <form
            onSubmit={onSubmit}
            className="hard-shadow rounded-md border-2 border-ink bg-card p-6 md:p-8"
            noValidate
          >
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Send a Message</div>
            <h3 className="mt-1 font-display text-3xl">Request a quote</h3>

            {/* Honeypot — hidden from users, bots fill it in */}
            <input
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              className="hidden"
              aria-hidden="true"
            />

            <div className="mt-6 space-y-4">
              <div>
                <label htmlFor="name" className="text-[11px] uppercase tracking-widest text-muted-foreground">Name</label>
                <input
                  id="name" name="name" type="text" required maxLength={100}
                  className="mt-1 w-full rounded-md border-2 border-ink bg-background px-3 py-2 text-base outline-none focus:border-accent"
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="email" className="text-[11px] uppercase tracking-widest text-muted-foreground">Email</label>
                  <input
                    id="email" name="email" type="email" required maxLength={255}
                    className="mt-1 w-full rounded-md border-2 border-ink bg-background px-3 py-2 text-base outline-none focus:border-accent"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="text-[11px] uppercase tracking-widest text-muted-foreground">Phone (optional)</label>
                  <input
                    id="phone" name="phone" type="tel" maxLength={40}
                    className="mt-1 w-full rounded-md border-2 border-ink bg-background px-3 py-2 text-base outline-none focus:border-accent"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="message" className="text-[11px] uppercase tracking-widest text-muted-foreground">Project details</label>
                <textarea
                  id="message" name="message" required rows={5} maxLength={2000}
                  placeholder="Tell Randy what you need — driveway, pond, culvert, aggregate delivery, etc."
                  className="mt-1 w-full rounded-md border-2 border-ink bg-background px-3 py-2 text-base outline-none focus:border-accent"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={status === 'sending'}
              className="mt-6 inline-flex w-full items-center justify-center rounded-md border-2 border-ink bg-ink px-4 py-3 text-sm font-semibold uppercase tracking-widest text-bone transition hover:bg-accent hover:text-accent-foreground disabled:opacity-60"
            >
              {status === 'sending' ? 'Sending…' : 'Send message'}
            </button>

            {status === 'success' && (
              <p className="mt-4 rounded-md border-2 border-ink bg-background px-3 py-2 text-sm text-foreground">
                Thanks — your message is on its way. Randy will get back to you shortly.
              </p>
            )}
            {status === 'error' && (
              <p className="mt-4 rounded-md border-2 border-destructive bg-background px-3 py-2 text-sm text-destructive">
                {errorMsg}
              </p>
            )}

            <p className="mt-4 text-xs text-muted-foreground">
              Ellis County &amp; surrounding — residential and commercial. Fully insured &amp; bonded.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t-2 border-ink/90 bg-ink text-bone">
      <div className="mx-auto grid max-w-7xl gap-10 px-5 py-14 md:grid-cols-3">
        <div>
          <div className="flex items-center gap-3">
            <img src={logo} alt="" aria-hidden className="h-12 w-12 object-contain invert" width={160} height={160} decoding="async" />
            <div>
              <div className="font-display text-2xl">Scott Ranch</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-bone/60">Sand &amp; Gravel, LLC</div>
            </div>
          </div>
          <p className="mt-4 max-w-xs text-sm text-bone/70">
            Family-owned excavation contractor serving Ellis County, Texas. Voted #1 in 2026.
          </p>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-bone/60">Services</div>
          <ul className="mt-3 grid grid-cols-2 gap-y-1 text-sm text-bone/85">
            {services.map((s) => <li key={s.t}>{s.t}</li>)}
          </ul>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-bone/60">Connect</div>
          <ul className="mt-3 space-y-2 text-sm">
            <li><a href={`tel:${TEL}`} className="hover:text-accent">{PHONE}</a></li>
            <li><a href="mailto:scottranchautosales@gmail.com" className="hover:text-accent">scottranchautosales@gmail.com</a></li>
            <li><a href={FB} target="_blank" rel="noopener noreferrer" className="hover:text-accent">Facebook</a></li>
            <li><a href={GBP} target="_blank" rel="noopener noreferrer" className="hover:text-accent">Google Business Profile</a></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-bone/15">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-5 py-5 text-xs text-bone/60">
          <div>© {new Date().getFullYear()} Scott Ranch Sand &amp; Gravel, LLC. All rights reserved.</div>
          <div>Ellis County, Texas</div>
        </div>
      </div>
    </footer>
  );
}
